import { useState, useEffect, useRef } from 'react';
import { MessageCircle, Send, X, History } from 'lucide-react';
import { IconButton } from '@hump/ui';
import { useChatStore } from '../../stores/chat';
import { useReaderStore } from '../../stores/reader';
import { isConfigured } from '../../lib/ai/config';

/**
 * 5.9 引用原文高亮：把 AI 回复中的 »原文« 包裹为带高亮样式的 <mark>。
 * 先转义 HTML 防注入，再用正则替换 »...« 为 <mark>...</mark>（背景色 oasis/20）。
 */
function renderQuoteHighlight(text: string): { __html: string } {
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
  // 匹配 »...«（非贪婪，允许跨普通字符，禁止跨 »）
  const html = escaped.replace(
    /»([^»]*)«/g,
    '<mark style="background-color: rgba(74, 139, 111, 0.2); border-radius: 2px; padding: 0 2px;">$1</mark>'
  );
  return { __html: html };
}

export function GuideChat({ docId }: { docId: string }) {
  const { messages, loading, error, loadChat, sendMessage } = useChatStore();
  const { guideOpen, setGuideOpen } = useReaderStore();
  const [input, setInput] = useState('');
  // 5.8 历史记录面板开关
  const [historyOpen, setHistoryOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const configured = isConfigured();

  useEffect(() => {
    if (guideOpen && docId) loadChat(docId);
  }, [guideOpen, docId, loadChat]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  async function handleSend() {
    if (!input.trim()) return;
    const content = input;
    setInput('');
    await sendMessage(docId, content);
  }

  if (!configured) return null;

  return (
    <>
      {!guideOpen && (
        <button onClick={() => setGuideOpen(true)}
          className="fixed bottom-4 right-4 md:bottom-6 md:right-6 w-12 h-12 rounded-full bg-oasis text-white shadow-lg flex items-center justify-center hover:bg-oasis-light transition-colors z-30"
          aria-label="问 AI">
          <MessageCircle className="w-5 h-5" />
        </button>
      )}
      {guideOpen && (
        <div className="fixed bottom-0 right-0 md:bottom-4 md:right-4 md:rounded-xl w-full md:w-96 h-[60vh] md:h-[500px] bg-white dark:bg-warm-dark shadow-lg flex flex-col z-40 border border-border-warm dark:border-border-warm-dark">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border-warm dark:border-border-warm-dark">
            <h3 className="text-sm font-medium text-charcoal dark:text-charcoal-light">📖 Guide 向导</h3>
            <div className="flex items-center gap-1">
              {/* 5.8 对话历史浏览：展开历史记录面板 */}
              <IconButton
                size="sm"
                onClick={() => setHistoryOpen((v) => !v)}
                aria-label="历史"
                title="历史对话"
              >
                <History className="w-4 h-4" />
              </IconButton>
              <IconButton size="sm" onClick={() => setGuideOpen(false)} aria-label="关闭">
                <X className="w-4 h-4" />
              </IconButton>
            </div>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3 relative">
            {historyOpen ? (
              /* 5.8 历史记录面板：从 idb-keyval chat:{docId} 读取（即 store.messages）
                 展示当前文档的全部历史问答，可滚动浏览 */
              <div className="absolute inset-0 bg-white dark:bg-warm-dark flex flex-col">
                <div className="flex items-center justify-between px-4 py-2 border-b border-border-warm dark:border-border-warm-dark">
                  <span className="text-xs font-medium text-charcoal/70 dark:text-charcoal-light/70">
                    历史对话（{messages.length}）
                  </span>
                  <button
                    onClick={() => setHistoryOpen(false)}
                    className="text-xs text-camel hover:underline"
                  >
                    返回
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
                  {messages.length === 0 ? (
                    <p className="text-charcoal/40 dark:text-charcoal-light/40 text-sm text-center py-8">
                      暂无历史对话
                    </p>
                  ) : (
                    messages.map((m) => (
                      <div
                        key={m.id}
                        className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[80%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap break-words ${
                            m.role === 'user'
                              ? 'bg-camel text-white'
                              : 'bg-warm-white dark:bg-border-warm-dark text-charcoal dark:text-charcoal-light'
                          }`}
                        >
                          <span className="block text-[10px] opacity-50 mb-0.5">
                            {m.role === 'user' ? '我' : 'Guide'} ·{' '}
                            {new Date(m.createdAt).toLocaleString('zh-CN')}
                          </span>
                          {m.role === 'assistant' ? (
                            /* 5.9 引用原文高亮：渲染 »原文« 为 <mark> */
                            <span dangerouslySetInnerHTML={renderQuoteHighlight(m.content)} />
                          ) : (
                            m.content
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            ) : (
              <>
                {messages.length === 0 && (
                  <p className="text-charcoal/40 dark:text-charcoal-light/40 text-sm text-center py-8">我是你的向导。读到哪里卡住了？问我。</p>
                )}
                {messages.map((m) => (
                  <div key={m.id} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap break-words ${
                      m.role === 'user' ? 'bg-camel text-white'
                        : 'bg-warm-white dark:bg-border-warm-dark text-charcoal dark:text-charcoal-light'}`}>
                      {m.role === 'assistant' ? (
                        /* 5.9 引用原文高亮：渲染 »原文« 为 <mark> */
                        <span dangerouslySetInnerHTML={renderQuoteHighlight(m.content)} />
                      ) : (
                        m.content
                      )}
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="flex justify-start">
                    <div className="bg-warm-white dark:bg-border-warm-dark rounded-lg px-3 py-2 text-sm text-charcoal/50">...</div>
                  </div>
                )}
                {error && <p className="text-error text-xs text-center">{error}</p>}
              </>
            )}
          </div>
          <div className="px-3 py-2 border-t border-border-warm dark:border-border-warm-dark flex gap-2 pb-[env(safe-area-inset-bottom)]">
            <input type="text" value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !loading && handleSend()} disabled={loading}
              placeholder="问 AI..." className="flex-1 rounded-md border border-border-warm dark:border-border-warm-dark bg-white dark:bg-warm-dark px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-oasis" />
            <IconButton size="sm" onClick={handleSend} disabled={loading || !input.trim()}>
              <Send className="w-4 h-4" />
            </IconButton>
          </div>
        </div>
      )}
    </>
  );
}
